import get from 'lodash/get'
import React from 'react'
import { SelectAsync } from '../../Select/SelectAsync'
import type { SelectAsyncProps } from '../../Select'
import { OptionComponent, SelectOptionCell, SelectOptionColumn } from '../../Select'
import { DisplayName, HorizontalStack, IconValueBlock, ShowIf, Status } from '@ztechapps/webui.util'
import { buildGraphqlQuery } from '@ztechapps/common.utils'
import type { GroupBase, MenuListProps } from 'react-select'
import { components } from 'react-select'
import { Row } from 'react-bootstrap'

//region GraphQL query
const documentFragment = `
    _id
    profile {
      _id
      name
      email
      picture
    }
    status
    company {
        _id
        name
        domain
      }
     `

export const query = buildGraphqlQuery('User', documentFragment)
//endregion

export const UserMenuList = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MenuListProps<Option, IsMulti, Group>
) => {
  const { children, ...rest } = props
  return (
    <components.MenuList {...rest} className="m-0 p-0">
      <Row className="m-0 bg-secondary-subtle fw-bold sticky-top ">
        <SelectOptionColumn>Company Name</SelectOptionColumn>
        <SelectOptionColumn>Contact Person</SelectOptionColumn>
        <SelectOptionColumn>Email</SelectOptionColumn>
      </Row>
      {children}
    </components.MenuList>
  )
}

export const UserOptionSimple = ({ data, ...rest }: any) => {
  //always set the isSelected to false so it wont highlight
  const contactName = get(data, 'profile.name') ?? ''
  const email = data?.profile?.email
  return (
    <components.Option {...{ ...rest, data, isSelected: false }} className="m-0 p-0">
      <ShowIf condition={data?.__isCreate}>
        <SelectOptionColumn>{email}</SelectOptionColumn>
      </ShowIf>
      <ShowIf condition={!data?.__isCreate}>
        <Row className="m-0">
          <SelectOptionColumn>
            <ShowIf condition={!data?.__isCreate}>
              <div className="fw-bold ">
                <Status value={data?.status} />{' '}
                <DisplayName company={data?.company} profile={data?.profile} linkTo={data?.linkTo} />
              </div>
            </ShowIf>
          </SelectOptionColumn>
          <SelectOptionCell value={contactName} className="small " />
          <SelectOptionCell value={email} className="small " icon="envelope" />
        </Row>
      </ShowIf>
    </components.Option>
  )
}

const OptionSimple = ({ data, ...rest }) => {
  const { name, email } = data?.profile ?? {}

  return (
    <OptionComponent
      {...{ ...rest, data }}
      label={
        <HorizontalStack className="justify-content-start align-items-center">
          <div>
            <div className="m-0">
              <Status value={data?.status} /> {name} <br />
              <div className="small">
                <IconValueBlock icon="envelope" value={email} />
              </div>
            </div>
          </div>
        </HorizontalStack>
      }
    />
  )
}

const defaultDataLoadOptions = (filter = {}) => ({
  query: get(query, 'allConfig.query'),
  filter: ({ input }) => {
    return {
      status: 'active',
      $or: [
        { 'profile.name': { $regex: input, $options: 'i' } },
        {
          'profile.email': {
            $regex: input,
            $options: 'i',
          },
        },
      ],
      ...filter,
    }
  },
  resultFieldName: 'allUser',
})

export type UserSelectProps = SelectAsyncProps

/**
 * A select component that displays user information.
 * This component wraps the SelectAsync, any valid react-select props can be used here
 */
export const UserSelect = ({ filter, dataLoadOptions, ...rest }: UserSelectProps) => {
  return (
    <SelectAsync
      {...{
        placeholder: 'Type the user name or email',
        dataLoadOptions: dataLoadOptions ?? defaultDataLoadOptions(filter),
        ...rest,
        cacheOptions: false,
        components: { Option: OptionSimple },
        labelKey: 'profile.name',
      }}
    />
  )
}
